import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import DashboardLayout from "../components/DashboardLayout";

// ─── Icons ───────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16 }) => {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  const icons = {
    heading: (
      <svg {...p}>
        <path d="M6 12h12M6 4v16M18 4v16" />
      </svg>
    ),
    text: (
      <svg {...p}>
        <line x1="17" y1="10" x2="3" y2="10" />
        <line x1="21" y1="6" x2="3" y2="6" />
        <line x1="21" y1="14" x2="3" y2="14" />
        <line x1="17" y1="18" x2="3" y2="18" />
      </svg>
    ),
    list: (
      <svg {...p}>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <circle cx="3" cy="6" r="1" fill="currentColor" />
        <circle cx="3" cy="12" r="1" fill="currentColor" />
        <circle cx="3" cy="18" r="1" fill="currentColor" />
      </svg>
    ),
    numbered: (
      <svg {...p}>
        <line x1="10" y1="6" x2="21" y2="6" />
        <line x1="10" y1="12" x2="21" y2="12" />
        <line x1="10" y1="18" x2="21" y2="18" />
        <path d="M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
      </svg>
    ),
    alert: (
      <svg {...p}>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
    info: (
      <svg {...p}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    ),
    divider: (
      <svg {...p}>
        <line x1="3" y1="12" x2="21" y2="12" />
      </svg>
    ),
    trash: (
      <svg {...p}>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14H6L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4h6v2" />
      </svg>
    ),
    grip: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="9" cy="6" r="1.6" />
        <circle cx="15" cy="6" r="1.6" />
        <circle cx="9" cy="12" r="1.6" />
        <circle cx="15" cy="12" r="1.6" />
        <circle cx="9" cy="18" r="1.6" />
        <circle cx="15" cy="18" r="1.6" />
      </svg>
    ),
    eye: (
      <svg {...p}>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    edit2: (
      <svg {...p}>
        <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
      </svg>
    ),
    plus: (
      <svg {...p}>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    ),
    up: (
      <svg {...p}>
        <polyline points="18 15 12 9 6 15" />
      </svg>
    ),
    down: (
      <svg {...p}>
        <polyline points="6 9 12 15 18 9" />
      </svg>
    ),
    collapse: (
      <svg {...p}>
        <polyline points="17 11 12 6 7 11" />
        <polyline points="17 18 12 13 7 18" />
      </svg>
    ),
    expand: (
      <svg {...p}>
        <polyline points="7 6 12 11 17 6" />
        <polyline points="7 13 12 18 17 13" />
      </svg>
    ),
    close: (
      <svg {...p}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
  };
  return icons[name] || null;
};

// ─── Config ──────────────────────────────────────────────────────────────────
const NAVY = "#2D2856";

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: "heading", color: NAVY },
  { type: "paragraph", label: "Paragraph", icon: "text", color: "#3b82f6" },
  { type: "bullet_list", label: "Bullet List", icon: "list", color: "#10b981" },
  { type: "numbered_list", label: "Numbered List", icon: "numbered", color: "#f59e0b" },
  { type: "callout_info", label: "Info Callout", icon: "info", color: "#06b6d4" },
  { type: "callout_warning", label: "Warning Callout", icon: "alert", color: "#f97316" },
  { type: "divider", label: "Divider", icon: "divider", color: "#94a3b8" },
];

const HEADING_LEVELS = ["H1", "H2", "H3"];
const DEFAULT_TITLE = "Event Guidelines";

const newId = () =>
  `block_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const defaultBlock = (type) => {
  const id = newId();
  switch (type) {
    case "heading": return { id, type, level: "H2", text: "Section Heading" };
    case "paragraph": return { id, type, text: "Add your guideline text here..." };
    case "bullet_list": return { id, type, items: ["First rule", "Second rule"] };
    case "numbered_list": return { id, type, items: ["Step one", "Step two"] };
    case "callout_info": return { id, type, title: "Note", text: "Important information for attendees." };
    case "callout_warning": return { id, type, title: "Warning", text: "Please read carefully before proceeding." };
    case "divider": return { id, type };
    default: return null;
  }
};

const starterBlocks = () => [
  { id: newId(), type: "heading", level: "H1", text: "Talk Guidelines" },
  { id: newId(), type: "paragraph", text: "Please review these guidelines before submitting your talk proposal." },
  { id: newId(), type: "bullet_list", items: ["Keep your talk focused and concise", "Respect the time limit"] },
];

// ─── Loading helpers (make saved data safe to render) ───────────────────────
const str = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

// Turns whatever the API returned for one block into a complete, safe block.
// (Missing fields such as `level` or `items` used to crash the page → blank screen.)
const normalizeBlock = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const type = typeof raw.type === "string" ? raw.type.trim().toLowerCase() : "";
  const id = newId();
  switch (type) {
    case "heading": {
      const lv = str(raw.level || "H2").toUpperCase();
      return { id, type, level: HEADING_LEVELS.includes(lv) ? lv : "H2", text: str(raw.text) };
    }
    case "paragraph":
      return { id, type, text: str(raw.text) };
    case "bullet_list":
    case "numbered_list": {
      const items = Array.isArray(raw.items)
        ? raw.items.map(str)
        : typeof raw.items === "string"
          ? raw.items.split("\n")
          : [];
      return { id, type, items: items.length ? items : [""] };
    }
    case "callout_info":
    case "callout_warning":
      return { id, type, title: str(raw.title), text: str(raw.text) };
    case "divider":
      return { id, type };
    default:
      return null;
  }
};

// Finds { title, blocks } anywhere in the response, whatever the nesting
// (guidelines / data.guidelines / JSON string / etc.).
const extractGuidelines = (data) => {
  const tryParse = (v) => {
    if (typeof v === "string") {
      try { return JSON.parse(v); } catch { return null; }
    }
    return v;
  };
  const find = (input, depth = 0) => {
    const node = tryParse(input);
    if (!node || typeof node !== "object" || depth > 6) return null;
    if (Array.isArray(node)) {
      return node.some((x) => x && typeof x === "object" && "type" in x)
        ? { title: undefined, blocks: node }
        : null;
    }
    if (Array.isArray(node.blocks)) {
      return { title: typeof node.title === "string" ? node.title : undefined, blocks: node.blocks };
    }
    for (const key of Object.keys(node)) {
      const r = find(node[key], depth + 1);
      if (r) return r;
    }
    return null;
  };
  return find(data);
};

const sanitizeBlockForApi = (block) => {
  const { id, ...rest } = block; // eslint-disable-line
  if (rest.type === "bullet_list" || rest.type === "numbered_list") {
    return { ...rest, items: rest.items.filter((item) => item.trim() !== "") };
  }
  return rest;
};

const blockSummary = (b) => {
  switch (b.type) {
    case "heading": return `${b.level} · ${b.text || "Untitled"}`;
    case "paragraph": return b.text || "Empty paragraph";
    case "bullet_list":
    case "numbered_list": {
      const f = b.items.filter((i) => i.trim());
      return f.length ? `${f.length} item${f.length > 1 ? "s" : ""} · ${f[0]}` : "Empty list";
    }
    case "callout_info":
    case "callout_warning": return b.title || b.text || "Callout";
    case "divider": return "Divider line";
    default: return "";
  }
};

// ─── Shared styles ───────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  padding: "8px 11px",
  fontSize: 14,
  border: "1px solid #e2e8f0",
  borderRadius: 5,
  outline: "none",
  background: "#f8fafc",
  color: "#1e293b",
  fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "all .15s",
};
const selectStyle = {
  padding: "8px 8px",
  fontSize: 13,
  border: "1px solid #e2e8f0",
  borderRadius: 5,
  outline: "none",
  background: "#f8fafc",
  color: "#1e293b",
  cursor: "pointer",
  fontFamily: "inherit",
};
const iconBtnStyle = (bg, color, disabled = false) => ({
  background: bg,
  border: "none",
  color,
  borderRadius: 5,
  width: 26,
  height: 26,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.4 : 1,
  flexShrink: 0,
  padding: 0,
});
const addItemBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 10px",
  fontSize: 12,
  color: NAVY,
  background: "#f1effa",
  border: "1px dashed #b9b4d9",
  borderRadius: 5,
  cursor: "pointer",
  width: "fit-content",
  fontFamily: "inherit",
  fontWeight: 600,
};
const cardStyle = {
  background: "#fff",
  borderRadius: 5,
  padding: "16px 16px",
  boxShadow: "0 4px 20px rgba(0,0,0,.06)",
};

function Btn({ children, onClick, variant = "primary", disabled = false, style: sx = {} }) {
  const variants = {
    primary: { background: NAVY, color: "#fff" },
    success: { background: "#10b981", color: "#fff" },
    outline: { background: "transparent", border: "2px solid #fff", color: "#fff" },
    light: { background: "#f1f5f9", color: "#475569" },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: "9px 16px",
        fontSize: "0.88rem",
        fontWeight: 600,
        borderRadius: 5,
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "inherit",
        opacity: disabled ? 0.55 : 1,
        transition: "all .2s",
        ...variants[variant],
        ...sx,
      }}
    >
      {children}
    </button>
  );
}

// ─── Block editors ───────────────────────────────────────────────────────────
const HeadingEditor = ({ block, onChange }) => (
  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
    <select
      value={block.level}
      onChange={(e) => onChange({ ...block, level: e.target.value })}
      style={selectStyle}
    >
      {HEADING_LEVELS.map((l) => (
        <option key={l} value={l}>{l}</option>
      ))}
    </select>
    <input
      value={block.text}
      onChange={(e) => onChange({ ...block, text: e.target.value })}
      placeholder="Heading text..."
      style={{
        ...inputStyle,
        fontWeight: 600,
        fontSize: block.level === "H1" ? 20 : block.level === "H2" ? 17 : 15,
        flex: 1,
        minWidth: 120,
      }}
    />
  </div>
);

const ParagraphEditor = ({ block, onChange }) => (
  <textarea
    value={block.text}
    onChange={(e) => onChange({ ...block, text: e.target.value })}
    placeholder="Write guideline text..."
    rows={3}
    style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }}
  />
);

const ListEditor = ({ block, onChange }) => {
  const update = (idx, val) => {
    const items = [...block.items];
    items[idx] = val;
    onChange({ ...block, items });
  };
  const add = () => onChange({ ...block, items: [...block.items, ""] });
  const remove = (idx) =>
    onChange({ ...block, items: block.items.filter((_, i) => i !== idx) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {block.items.map((item, idx) => (
        <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ color: "#94a3b8", fontSize: 13, minWidth: 20, textAlign: "right" }}>
            {block.type === "numbered_list" ? `${idx + 1}.` : "•"}
          </span>
          <input
            value={item}
            onChange={(e) => update(idx, e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={`Item ${idx + 1}`}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={() => remove(idx)} style={iconBtnStyle("#fee2e2", "#ef4444")} title="Remove">
            <Icon name="trash" size={13} />
          </button>
        </div>
      ))}
      <button onClick={add} style={addItemBtnStyle}>
        <Icon name="plus" size={13} /> Add item
      </button>
    </div>
  );
};

const CalloutEditor = ({ block, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    <input
      value={block.title}
      onChange={(e) => onChange({ ...block, title: e.target.value })}
      placeholder="Callout title..."
      style={{ ...inputStyle, fontWeight: 600 }}
    />
    <textarea
      value={block.text}
      onChange={(e) => onChange({ ...block, text: e.target.value })}
      placeholder="Callout message..."
      rows={2}
      style={{ ...inputStyle, resize: "vertical" }}
    />
  </div>
);

const DividerEditor = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #cbd5e1, transparent)" }} />
    <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>Divider</span>
    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #cbd5e1, transparent)" }} />
  </div>
);

// ─── Preview ─────────────────────────────────────────────────────────────────
const PreviewBlock = ({ block }) => {
  switch (block.type) {
    case "heading": {
      const Tag = (block.level || "H2").toLowerCase();
      const sizes = { h1: 24, h2: 19, h3: 16 };
      return (
        <Tag style={{ margin: "0 0 2px 0", fontWeight: 700, color: "#1a1a2e", fontSize: sizes[Tag] || 19 }}>
          {block.text}
        </Tag>
      );
    }
    case "paragraph":
      return <p style={{ margin: 0, color: "#475569", lineHeight: 1.7, fontSize: 14 }}>{block.text}</p>;
    case "bullet_list":
      return (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {block.items.filter((i) => i.trim()).map((item, i) => (
            <li key={i} style={{ color: "#475569", fontSize: 14, lineHeight: 1.7 }}>{item}</li>
          ))}
        </ul>
      );
    case "numbered_list":
      return (
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          {block.items.filter((i) => i.trim()).map((item, i) => (
            <li key={i} style={{ color: "#475569", fontSize: 14, lineHeight: 1.7 }}>{item}</li>
          ))}
        </ol>
      );
    case "callout_info":
      return (
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderLeft: "4px solid #3b82f6", borderRadius: 5, padding: "10px 14px" }}>
          <div style={{ fontWeight: 600, color: "#1d4ed8", fontSize: 13, marginBottom: 4 }}>ℹ️ {block.title}</div>
          <div style={{ color: "#1e40af", fontSize: 13 }}>{block.text}</div>
        </div>
      );
    case "callout_warning":
      return (
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderLeft: "4px solid #f97316", borderRadius: 5, padding: "10px 14px" }}>
          <div style={{ fontWeight: 600, color: "#c2410c", fontSize: 13, marginBottom: 4 }}>⚠️ {block.title}</div>
          <div style={{ color: "#9a3412", fontSize: 13 }}>{block.text}</div>
        </div>
      );
    case "divider":
      return <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "4px 0" }} />;
    default:
      return null;
  }
};

// ─── Viewport hook ───────────────────────────────────────────────────────────
const useViewportWidth = () => {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
};

// ─── Main component ──────────────────────────────────────────────────────────
export default function GuidelinesBuilder() {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const width = useViewportWidth();
  const isMobile = width < 768;
  const showPreviewPane = width >= 1100;

  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [blocks, setBlocks] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const [activeTab, setActiveTab] = useState("editor");
  const [showBlockPicker, setShowBlockPicker] = useState(false);

  const [pageLoading, setPageLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Was a saved version loaded from the server?
  const [hasExisting, setHasExisting] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  // Drag state
  const [dragSource, setDragSource] = useState(null); // { kind: "block", id } | { kind: "new", type }
  const [dropIndex, setDropIndex] = useState(null);
  const dropIndexRef = useRef(null);
  const dragSourceRef = useRef(null);
  const editorRef = useRef(null);

  const snapshotOf = (t, b) =>
    JSON.stringify({ title: t.trim(), blocks: b.map(sanitizeBlockForApi) });

  const currentSnapshot = useMemo(() => snapshotOf(title, blocks), [title, blocks]);
  const isDirty = savedSnapshot === null ? true : savedSnapshot !== currentSnapshot;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Warn before leaving with unsaved edits
  useEffect(() => {
    if (!isDirty || !hasExisting) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty, hasExisting]);

  // ── Load saved guidelines ──
  useEffect(() => {
    if (!eventId) { setPageLoading(false); return; }
    const load = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/event_guidelines/${eventId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (response.ok) {
          const data = await response.json();
          const found = extractGuidelines(data);
          const loaded = (found?.blocks || []).map(normalizeBlock).filter(Boolean);

          if (loaded.length > 0) {
            const loadedTitle = found.title && found.title.trim() ? found.title : DEFAULT_TITLE;
            setBlocks(loaded);
            setTitle(loadedTitle);
            setHasExisting(true);
            setSavedSnapshot(snapshotOf(loadedTitle, loaded));
          } else {
            setBlocks(starterBlocks());
          }
        } else if (response.status === 404) {
          setBlocks(starterBlocks());
        } else {
          setError("Failed to load guidelines. You can still create new ones.");
          setBlocks(starterBlocks());
        }
      } catch (err) {
        console.error("Error fetching guidelines:", err);
        setError("Network error while loading guidelines. You can still create new ones.");
        setBlocks(starterBlocks());
      } finally {
        setPageLoading(false);
      }
    };
    load();
  }, [eventId]); // eslint-disable-line

  // ── Block operations ──
  const addBlock = (type) => {
    setBlocks((b) => [...b, defaultBlock(type)]);
    setShowBlockPicker(false);
    setTimeout(() => {
      const el = editorRef.current;
      if (el && !isMobile) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, 50);
  };
  const updateBlock = (id, updated) =>
    setBlocks((b) => b.map((bl) => (bl.id === id ? updated : bl)));
  const removeBlock = (id) => setBlocks((b) => b.filter((bl) => bl.id !== id));
  const moveBlock = (from, to) => {
    setBlocks((b) => {
      const arr = [...b];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  };
  const toggleCollapse = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));
  const collapseAll = () =>
    setCollapsed(Object.fromEntries(blocks.map((b) => [b.id, true])));
  const expandAll = () => setCollapsed({});

  // ── Drag and drop ──
  const startDrag = (src) => {
    dragSourceRef.current = src;
    // Collapse cards a tick later so the browser can pick up the drag first
    setTimeout(() => setDragSource(src), 0);
  };
  const endDrag = () => {
    dragSourceRef.current = null;
    dropIndexRef.current = null;
    setDragSource(null);
    setDropIndex(null);
  };
  const setDrop = (i) => {
    if (dropIndexRef.current !== i) {
      dropIndexRef.current = i;
      setDropIndex(i);
    }
  };

  const onBlockDragStart = (e, id) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
    const card = e.currentTarget.closest("[data-glblock]");
    if (card) e.dataTransfer.setDragImage(card, 16, 16);
    startDrag({ kind: "block", id });
  };

  const onPaletteDragStart = (e, type) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", type);
    startDrag({ kind: "new", type });
  };

  const performDrop = () => {
    const src = dragSourceRef.current;
    const target = dropIndexRef.current ?? blocks.length;
    if (!src) return;

    if (src.kind === "new") {
      const nb = defaultBlock(src.type);
      if (nb) {
        setBlocks((b) => {
          const arr = [...b];
          arr.splice(Math.min(target, arr.length), 0, nb);
          return arr;
        });
      }
    } else {
      const from = blocks.findIndex((b) => b.id === src.id);
      if (from !== -1 && target !== from && target !== from + 1) {
        const insertAt = target > from ? target - 1 : target;
        moveBlock(from, insertAt);
      }
    }
    endDrag();
  };

  // Auto-scroll the editor while dragging near its top/bottom edge
  const onEditorDragOver = (e) => {
    if (!dragSourceRef.current) return;
    e.preventDefault();
    const el = editorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = e.clientY;
    if (y - rect.top < 70) el.scrollTop -= 16;
    else if (rect.bottom - y < 70) el.scrollTop += 16;
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!eventId) { setError("Event ID is missing. Cannot save guidelines."); return; }
    if (blocks.length === 0) { setError("Please add at least one block before submitting."); return; }
    setSubmitting(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const sanitizedBlocks = blocks.map(sanitizeBlockForApi).filter((block) => {
        if (block.type === "bullet_list" || block.type === "numbered_list") return block.items.length > 0;
        return true;
      });
      const payload = {
        eventId: Number(eventId),
        guidelines: { title: title.trim() || DEFAULT_TITLE, blocks: sanitizedBlocks },
      };
      const response = await fetch(`${API_BASE_URL}/event_guidelines`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.message || `Server error: ${response.status}`);
      }
      navigate("/dashboard", {
        state: {
          message: hasExisting
            ? "Event guidelines updated successfully!"
            : "Create event guidelines successfully!",
        },
      });
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.message || "Failed to save guidelines. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const typeMeta = (type) => BLOCK_TYPES.find((t) => t.type === type) || {};

  // ── Loading screen ──
  if (pageLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f4f5fb", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#475569" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", border: "4px solid #e2e8f0", borderTopColor: NAVY, animation: "glbspin 0.8s linear infinite" }} />
        <style>{`@keyframes glbspin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ margin: 0, fontWeight: 500 }}>Loading guidelines…</p>
      </div>
    );
  }

  const statusText = hasExisting
    ? isDirty ? "Unsaved changes" : "Saved"
    : "Not submitted yet";
  const statusColor = hasExisting && !isDirty ? "#10b981" : "#fbbf24";

  const gridClass = showPreviewPane
    ? "glb-grid glb-three"
    : isMobile
      ? "glb-grid glb-one"
      : "glb-grid glb-two";

  const showEditor = showPreviewPane || activeTab === "editor";
  const showPreview = showPreviewPane || activeTab === "preview";
  const showPalette = !isMobile && showEditor;

  const scrollAreaStyle = isMobile
    ? {}
    : { maxHeight: "calc(100vh - 250px)", minHeight: 320, overflowY: "auto", paddingRight: 4 };

  // ── Editor list ──
  const editorList = (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", borderBottom: "2px solid #f1f5f9", paddingBottom: 10, marginBottom: 12 }}>
        <div>
          <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>
            Blocks <span style={{ color: "#94a3b8", fontWeight: 500 }}>({blocks.length})</span>
          </h2>
          {!isMobile && (
            <div style={{ color: "#94a3b8", fontSize: "0.76rem", marginTop: 2 }}>
              Drag the ⋮⋮ handle to reorder — cards fold up while you drag.
            </div>
          )}
        </div>
        {blocks.length > 0 && (
          <div style={{ display: "flex", gap: 6 }}>
            <Btn variant="light" onClick={collapseAll} style={{ padding: "6px 10px", fontSize: "0.78rem" }}>
              <Icon name="collapse" size={13} /> Collapse all
            </Btn>
            <Btn variant="light" onClick={expandAll} style={{ padding: "6px 10px", fontSize: "0.78rem" }}>
              <Icon name="expand" size={13} /> Expand all
            </Btn>
          </div>
        )}
      </div>

      <div
        ref={editorRef}
        style={scrollAreaStyle}
        onDragOver={onEditorDragOver}
        onDrop={(e) => { e.preventDefault(); performDrop(); }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDrop(null);
        }}
      >
        {blocks.length === 0 && (
          <div style={{ textAlign: "center", padding: "44px 20px", color: "#94a3b8", border: "2px dashed #e2e8f0", borderRadius: 5, background: "#f8fafc" }}>
            <div style={{ fontSize: 34, marginBottom: 8 }}>📋</div>
            <div style={{ fontWeight: 600, marginBottom: 4, color: "#64748b" }}>No blocks yet</div>
            <div style={{ fontSize: 13, marginBottom: 14 }}>
              {isMobile ? "Tap the + button to add a block" : "Click or drag a block type from the left"}
            </div>
            <Btn onClick={() => setBlocks(starterBlocks())}>Use starter template</Btn>
          </div>
        )}

        {blocks.map((block, idx) => {
          const meta = typeMeta(block.type);
          const compact = !!collapsed[block.id] || !!dragSource;
          const isBeingDragged = dragSource?.kind === "block" && dragSource.id === block.id;
          const lineTop = dragSource && dropIndex === idx;
          const lineBottom = dragSource && dropIndex === blocks.length && idx === blocks.length - 1;

          return (
            <div
              key={block.id}
              data-glblock
              className="glb-card"
              onDragOver={(e) => {
                if (!dragSourceRef.current) return;
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setDrop(e.clientY < r.top + r.height / 2 ? idx : idx + 1);
              }}
              style={{
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderLeft: `4px solid ${meta.color || NAVY}`,
                borderRadius: 5,
                marginBottom: 8,
                opacity: isBeingDragged ? 0.4 : 1,
                boxShadow: lineTop
                  ? `0 -4px 0 0 ${NAVY}`
                  : lineBottom
                    ? `0 4px 0 0 ${NAVY}`
                    : "0 1px 4px rgba(0,0,0,.04)",
                transition: "box-shadow .12s, opacity .12s",
              }}
            >
              {/* Card header */}
              <div
                onClick={() => !dragSource && toggleCollapse(block.id)}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", userSelect: "none" }}
              >
                {!isMobile && (
                  <div
                    draggable
                    onDragStart={(e) => onBlockDragStart(e, block.id)}
                    onDragEnd={endDrag}
                    onClick={(e) => e.stopPropagation()}
                    title="Drag to reorder"
                    style={{ color: "#94a3b8", cursor: "grab", display: "flex", padding: "4px 2px" }}
                  >
                    <Icon name="grip" size={16} />
                  </div>
                )}
                <span style={{ color: meta.color, display: "flex" }}>
                  <Icon name={meta.icon} size={14} />
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, flexShrink: 0 }}>
                  {meta.label}
                </span>
                {compact && (
                  <span style={{ flex: 1, minWidth: 0, color: "#475569", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {blockSummary(block)}
                  </span>
                )}
                {!compact && <span style={{ flex: 1 }} />}

                <div style={{ display: "flex", gap: 4, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => idx > 0 && moveBlock(idx, idx - 1)} style={iconBtnStyle("#f1f5f9", "#64748b", idx === 0)} title="Move up" disabled={idx === 0}>
                    <Icon name="up" size={12} />
                  </button>
                  <button onClick={() => idx < blocks.length - 1 && moveBlock(idx, idx + 1)} style={iconBtnStyle("#f1f5f9", "#64748b", idx === blocks.length - 1)} title="Move down" disabled={idx === blocks.length - 1}>
                    <Icon name="down" size={12} />
                  </button>
                  <button onClick={() => removeBlock(block.id)} style={iconBtnStyle("#fee2e2", "#ef4444")} title="Delete block">
                    <Icon name="trash" size={12} />
                  </button>
                  <button
                    onClick={() => toggleCollapse(block.id)}
                    style={iconBtnStyle("transparent", "#94a3b8")}
                    title={collapsed[block.id] ? "Expand" : "Collapse"}
                  >
                    <span style={{ display: "flex", transform: collapsed[block.id] ? "none" : "rotate(180deg)", transition: "transform .2s" }}>
                      <Icon name="down" size={14} />
                    </span>
                  </button>
                </div>
              </div>

              {/* Card body */}
              {!compact && (
                <div style={{ padding: "2px 12px 12px" }}>
                  {block.type === "heading" && <HeadingEditor block={block} onChange={(b) => updateBlock(block.id, b)} />}
                  {block.type === "paragraph" && <ParagraphEditor block={block} onChange={(b) => updateBlock(block.id, b)} />}
                  {(block.type === "bullet_list" || block.type === "numbered_list") && <ListEditor block={block} onChange={(b) => updateBlock(block.id, b)} />}
                  {(block.type === "callout_info" || block.type === "callout_warning") && <CalloutEditor block={block} onChange={(b) => updateBlock(block.id, b)} />}
                  {block.type === "divider" && <DividerEditor />}
                </div>
              )}
            </div>
          );
        })}

        {/* End drop zone (only while dragging) */}
        {dragSource && blocks.length > 0 && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrop(blocks.length); }}
            style={{
              minHeight: 48,
              border: `2px dashed ${dropIndex === blocks.length ? NAVY : "#e2e8f0"}`,
              background: dropIndex === blocks.length ? "rgba(45,40,86,.06)" : "transparent",
              borderRadius: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: dropIndex === blocks.length ? NAVY : "#94a3b8",
              fontSize: 13,
              fontStyle: "italic",
              transition: "all .15s",
            }}
          >
            Drop here to place at the end
          </div>
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>
    </div>
  );

  // ── Preview pane ──
  const previewPane = (
    <div style={cardStyle}>
      <div style={{ borderBottom: "2px solid #f1f5f9", paddingBottom: 10, marginBottom: 12 }}>
        <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>Live Preview</h2>
        <div style={{ color: "#94a3b8", fontSize: "0.76rem", marginTop: 2 }}>
          How attendees will see these guidelines
        </div>
      </div>
      <div style={{ ...scrollAreaStyle, display: "flex", flexDirection: "column", gap: 12 }}>
        {title.trim() && (
          <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 1.2, textTransform: "uppercase" }}>
            {title}
          </div>
        )}
        {blocks.length === 0 ? (
          <div style={{ color: "#94a3b8", fontStyle: "italic", fontSize: 14 }}>
            No content yet — add blocks in the editor.
          </div>
        ) : (
          blocks.map((block) => <PreviewBlock key={block.id} block={block} />)
        )}
      </div>
    </div>
  );

  // ── Palette ──
  const palette = (
    <div style={{ ...cardStyle, position: "sticky", top: 12 }}>
      <div style={{ borderBottom: "2px solid #f1f5f9", paddingBottom: 10, marginBottom: 12 }}>
        <h2 style={{ color: "#475569", fontSize: "1rem", margin: 0 }}>Add Block</h2>
        <div style={{ color: "#94a3b8", fontSize: "0.76rem", marginTop: 2 }}>
          Click to add, or drag into place
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {BLOCK_TYPES.map((bt) => (
          <div
            key={bt.type}
            className="glb-palette-btn"
            draggable
            onDragStart={(e) => onPaletteDragStart(e, bt.type)}
            onDragEnd={endDrag}
            onClick={() => addBlock(bt.type)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 5, cursor: "grab", fontSize: 13, fontWeight: 500, color: "#334155", userSelect: "none", transition: "all .15s" }}
          >
            <span style={{ color: bt.color, display: "flex" }}><Icon name={bt.icon} size={14} /></span>
            <span style={{ flex: 1 }}>{bt.label}</span>
            <span style={{ color: "#cbd5e1", display: "flex" }}><Icon name="grip" size={13} /></span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardLayout currentSection="speaking">
      <div className="glb-root" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: "100vh", background: "#f7f7f5", color: "#334155" }}>
        <style>{`
          @keyframes glbspin { to { transform: rotate(360deg); } }
          @keyframes glbslide { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          .glb-root * { box-sizing: border-box; }
          .glb-root input:focus, .glb-root textarea:focus, .glb-root select:focus {
            border-color: ${NAVY} !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(45,40,86,.1);
          }
          .glb-palette-btn:hover { background: #f1effa !important; border-color: ${NAVY} !important; color: ${NAVY} !important; }
          .glb-card:hover { border-color: #cfd4dc; }
          .glb-grid { display: grid; gap: 18px; align-items: start; }
          .glb-three { grid-template-columns: 210px minmax(0, 1fr) minmax(0, 1fr); }
          .glb-two { grid-template-columns: 210px minmax(0, 1fr); }
          .glb-one { grid-template-columns: minmax(0, 1fr); }
          .glb-root ::-webkit-scrollbar { width: 5px; height: 5px; }
          .glb-root ::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 3px; }
          .glb-root ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        `}</style>

        <div style={{ maxWidth: 1700, margin: "0 auto", padding: "16px 14px" }}>
          {/* Header */}
          <div style={{ background: NAVY, color: "#fff", padding: isMobile ? "14px 14px" : "18px 22px", borderRadius: 5, marginBottom: 16, boxShadow: "0 10px 30px rgba(45,40,86,.25)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 2 }}>
                <span style={{ color: "#c7c3e6", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>
                  Guidelines Builder
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,.12)", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor, display: "inline-block" }} />
                  {statusText}
                </span>
              </div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Guidelines title..."
                style={{ background: "transparent", border: "none", borderBottom: "1px dashed rgba(255,255,255,.3)", borderRadius: 0, outline: "none", color: "#fff", fontSize: isMobile ? 18 : 22, fontWeight: 800, fontFamily: "inherit", width: "100%", maxWidth: 520, padding: "2px 0" }}
              />
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {!showPreviewPane && (
                <>
                  <Btn variant="outline" onClick={() => setActiveTab("editor")} style={{ background: activeTab === "editor" ? "#fff" : "transparent", color: activeTab === "editor" ? NAVY : "#fff" }}>
                    <Icon name="edit2" size={14} /> Editor
                  </Btn>
                  <Btn variant="outline" onClick={() => setActiveTab("preview")} style={{ background: activeTab === "preview" ? "#fff" : "transparent", color: activeTab === "preview" ? NAVY : "#fff" }}>
                    <Icon name="eye" size={14} /> Preview
                  </Btn>
                </>
              )}
              <Btn variant="success" onClick={handleSubmit} disabled={submitting || blocks.length === 0}>
                {submitting ? (
                  <>
                    <span style={{ display: "inline-block", width: 13, height: 13, borderRadius: "50%", border: "2px solid rgba(255,255,255,.5)", borderTopColor: "#fff", animation: "glbspin 0.8s linear infinite" }} />
                    Saving...
                  </>
                ) : hasExisting ? "Update Guidelines" : "Submit"}
              </Btn>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", padding: "10px 14px", borderRadius: 5, fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <span style={{ flex: 1 }}>{error}</span>
              <button onClick={() => setError("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
          )}

          {/* Main grid */}
          <div className={gridClass}>
            {showPalette && palette}
            {showEditor && editorList}
            {showPreview && previewPane}
          </div>
        </div>

        {/* Mobile FAB */}
        {isMobile && activeTab === "editor" && (
          <button
            onClick={() => setShowBlockPicker(true)}
            title="Add block"
            style={{ position: "fixed", bottom: 24, right: 20, width: 52, height: 52, borderRadius: "50%", background: NAVY, border: "none", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 4px 16px rgba(45,40,86,.4)", zIndex: 100 }}
          >
            <Icon name="plus" size={22} />
          </button>
        )}

        {/* Mobile block picker */}
        {isMobile && showBlockPicker && (
          <>
            <div onClick={() => setShowBlockPicker(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 200 }} />
            <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#fff", borderRadius: "20px 20px 0 0", padding: "16px 16px 32px", zIndex: 300, animation: "glbslide .25s ease" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: 1.2, textTransform: "uppercase" }}>Add Block</div>
                <button onClick={() => setShowBlockPicker(false)} style={{ background: "#f1f5f9", border: "none", borderRadius: 5, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#64748b" }}>
                  <Icon name="close" size={15} />
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                {BLOCK_TYPES.map((bt) => (
                  <button
                    key={bt.type}
                    onClick={() => addBlock(bt.type)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 5, cursor: "pointer", fontSize: 13, fontWeight: 500, color: "#334155", fontFamily: "inherit", textAlign: "left", touchAction: "manipulation" }}
                  >
                    <span style={{ color: bt.color }}><Icon name={bt.icon} size={16} /></span>
                    {bt.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}